const FALLBACK_CURRENCY = "GHS";
const FALLBACK_NAME = "Supplier";

const cleanName = (name) => {
  const trimmed = String(name || "").trim();
  if (!trimmed) return FALLBACK_NAME;

  const atIndex = trimmed.indexOf("@");
  const withoutEmail = atIndex > 0 ? trimmed.slice(0, atIndex).trim() : trimmed;

  return withoutEmail || FALLBACK_NAME;
};

const joinAddress = (address) =>
  [address?.address1, address?.city].filter(Boolean).join(", ");

export const mapSupplier = (dto) => {
  const address = dto?.address || {};

  const latitude = Number.isFinite(address.latitude) ? address.latitude : null;
  const longitude = Number.isFinite(address.longitude) ? address.longitude : null;

  const meters = dto?.distanceToLocationMeters;
  const pricePerKg = dto?.pricePerKg?.value ?? dto?.avgPricePerKg ?? null;

  return {
    id: dto?.id,
    name: cleanName(dto?.name),
    logoUrl: dto?.referenceUrlLogo || null,
    bannerUrl: dto?.bannerImageUrl || null,
    latitude,
    longitude,
    hasCoordinates: latitude !== null && longitude !== null,
    distanceKm: Number.isFinite(meters) ? meters / 1000 : null,
    pricePerKg: Number.isFinite(pricePerKg) ? Number(pricePerKg) : null,
    currency:
      dto?.pricePerKg?.currency ?? dto?.avgPriceCurrency ?? FALLBACK_CURRENCY,
    rating: dto?.avgRating ?? 0,
    totalRatings: dto?.totalRatings ?? 0,
    online: dto?.status === "online",
    addressLine: joinAddress(address),
    saved: dto?.saved ?? null,
  };
};

export const formatPriceLabel = (price, currency) => {
  if (!Number.isFinite(price)) return "";

  return `${currency || FALLBACK_CURRENCY} ${price.toFixed(2)}`;
};

export const formatDistance = (distanceKm) => {
  if (!Number.isFinite(distanceKm)) return "";

  return `${distanceKm.toFixed(1)} km`;
};

export const toMarkers = (suppliers) => {
  const mappable = suppliers.filter((supplier) => supplier.hasCoordinates);

  const prices = mappable
    .map((supplier) => supplier.pricePerKg)
    .filter((price) => Number.isFinite(price));

  const cheapest = prices.length ? Math.min(...prices) : null;
  const pricesVary = prices.length > 1 && Math.max(...prices) !== cheapest;

  return mappable.map((supplier) => ({
    ...supplier,
    priceLabel: formatPriceLabel(supplier.pricePerKg, supplier.currency),
    unitLabel: "pKG",
    highlighted: pricesVary && supplier.pricePerKg === cheapest,
  }));
};

export const groupOffersByVolume = (rows) => {
  const groups = new Map();

  rows.forEach((row) => {
    if (!row?.inStock) return;

    const volume = Number(row.volume);
    const supplierId = row.supplier?.id;
    const price = Number(row.amount?.value);

    if (!Number.isFinite(volume) || supplierId === undefined) return;

    const group = groups.get(volume) || {
      volume,
      supplierIds: new Set(),
      prices: new Map(),
      price: null,
      currency: row.amount?.currency || FALLBACK_CURRENCY,
    };

    group.supplierIds.add(supplierId);

    if (Number.isFinite(price) && price > 0) {
      const best = group.prices.get(supplierId);

      if (best === undefined || price < best) group.prices.set(supplierId, price);

      if (group.price === null || price < group.price) {
        group.price = price;
        group.currency = row.amount?.currency || group.currency;
      }
    }

    groups.set(volume, group);
  });

  return groups;
};

export const cheapestForSuppliers = (group, supplierIds) => {
  let cheapest = null;

  supplierIds.forEach((id) => {
    const price = group.prices.get(id);

    if (price !== undefined && (cheapest === null || price < cheapest)) {
      cheapest = price;
    }
  });

  return cheapest;
};
